import cn from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useMutation, useQueryClient } from 'react-query';
import {
  ButtonUI,
  Checkbox,
  Loader,
  ModalUI,
  showNotification,
  TaptapList,
  useQueryFetch
} from 'sonata-react-components';
import { getUserName } from 'sonata-static-storage/lib/helpers/user.helpers';

import { deleteUserAlias, getAliasById, getUserAlias, updateUserAlias } from '../../../services/alias.resources';
import { CreateUserAlias } from './CreateUserAlias';

import './userAlias.styles.scss';

import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';

type Props = {
  onClose: () => void;
  user: UserInterface;
};

type State = 'list' | 'create';

type TaptapListItem = {
  id: string;
  name: string;
  email: string;
  alias: UserInterface<string>;
};

function UserAlias({ onClose, user }: Props) {
  const queryClient = useQueryClient();
  const [view, setView] = useState<State>('list');
  const { formatMessage } = useIntl();

  const modalRef: any = useRef();
  useEffect(() => {
    modalRef?.current?.openModal();
  }, [modalRef]);

  const aliasesToken = {};
  const { data: aliases = [], isLoading } = useQueryFetch<UserInterface[]>(
    ['aliases', user],
    async () => {
      const currentAliases = await getUserAlias(user, aliasesToken);
      return currentAliases.filter((alias: UserInterface) => !alias.aliasConfiguration.deleted);
    },
    {
      enabled: true
    }
  );

  const aliasesDeleteToken = {};
  const aliasDeleteMutation = useMutation({
    mutationFn: (aliasToDelete: UserInterface) => deleteUserAlias(aliasToDelete, aliasesDeleteToken),
    mutationKey: ['delete-user-alias'],
    onError: (_err, aliasFailedToDelete: UserInterface, context?: { previousAliases: UserInterface[] }) => {
      queryClient.setQueryData(['aliases', user], (old: UserInterface[] = []) => {
        const itemToRestore = context?.previousAliases?.find(a => a._id === aliasFailedToDelete._id);

        if (itemToRestore) {
          return [...old, itemToRestore];
        }

        return old;
      });

      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Alias user could not be removed, please try again',
          id: 'menu.userAlias.alert.failDeletion'
        })
      );
    },
    onMutate: async aliasToDelete => {
      await queryClient.cancelQueries(['aliases', user]);
      const previousAliases = queryClient.getQueryData(['aliases', user]);

      queryClient.setQueryData(['aliases', user], (old: UserInterface[] = []) => {
        return old.filter(alias => alias._id !== aliasToDelete._id);
      });

      showNotification(
        'success',
        formatMessage({
          defaultMessage: 'Alias deleted successfully',
          id: 'menu.userAlias.notify.successDeletion'
        })
      );

      return { previousAliases } as { previousAliases: UserInterface[] };
    },
    onSettled: () => {
      if (queryClient.isMutating({ mutationKey: ['delete-user-alias'] }) === 1) {
        queryClient.invalidateQueries(['aliases', user]);
      }
    }
  });

  const handleAliasDelete = (alias: UserInterface) => {
    aliasDeleteMutation.mutate(alias);
  };

  const aliasUpdateToken = {};
  const getAliasUpdateToken = {};

  const aliasActiveMutation = useMutation({
    mutationFn: async (aliasOptimistic: UserInterface) => {
      const currentAlias = await getAliasById(aliasOptimistic._id!, getAliasUpdateToken);
      const newAliasConfig = {
        ...currentAlias,
        aliasConfiguration: {
          ...currentAlias.aliasConfiguration,
          active: aliasOptimistic.aliasConfiguration.active
        },
        id: aliasOptimistic._id
      };
      return updateUserAlias(newAliasConfig, aliasUpdateToken);
    },
    mutationKey: ['toggle-alias-active'],
    onError: (_err, _newAlias: UserInterface, context?: { previousAliases: UserInterface[] }) => {
      queryClient.setQueryData(['aliases', user], (old: UserInterface[] = []) => {
        return old.map(alias => {
          if (alias._id === _newAlias._id) {
            const originalItem = context?.previousAliases?.find(a => a._id === _newAlias._id);
            if (originalItem) {
              return originalItem;
            }

            return {
              ...alias,
              aliasConfiguration: {
                ...alias.aliasConfiguration,
                active: !_newAlias.aliasConfiguration.active
              }
            };
          }

          return alias;
        });
      });
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Failed to enable/disable the alias user, please try again',
          id: 'menu.userAlias.alert.failActiveAlias'
        })
      );
    },
    onMutate: async newAlias => {
      await queryClient.cancelQueries(['aliases', user]);
      const previousAliases = queryClient.getQueryData(['aliases', user]);

      queryClient.setQueryData(['aliases', user], (old: UserInterface[] = []) => {
        return old?.map(alias => {
          if (alias._id === newAlias._id) {
            return newAlias;
          }
          return alias;
        });
      });

      return { previousAliases } as { previousAliases: UserInterface[] };
    },
    onSettled: () => {
      if (queryClient.isMutating({ mutationKey: ['toggle-alias-active'] }) === 1) {
        queryClient.invalidateQueries(['aliases', user]);
      }
    }
  });

  const handleAliasActiveChange = (currentAlias: UserInterface) => {
    const newAlias = {
      ...currentAlias,
      aliasConfiguration: {
        ...currentAlias.aliasConfiguration,
        active: !currentAlias.aliasConfiguration.active
      }
    };
    aliasActiveMutation.mutate(newAlias);
  };

  const handleAliasCreation = () => {
    setView('list');
  };

  const aliasesCompositionData = useMemo(
    () =>
      aliases.map(alias => ({
        alias: alias,
        email: alias.email,
        id: alias._id!,
        name: alias.name
      })),
    [aliases]
  );

  return (
    <ModalUI
      closeModalCallback={onClose}
      ref={modalRef}
      title={
        view === 'list'
          ? formatMessage({
              defaultMessage: 'Alias management',
              id: 'menu.userAlias.modalTitle'
            })
          : formatMessage({
              defaultMessage: 'Create new alias',
              id: 'menu.userAlias.createNew.title'
            })
      }
      width="625px"
    >
      {isLoading ? (
        <Loader className="userAliasModal__spinner" id={'user-alias-spinner'} type="landing" variant="default">
          <Loader.Title className="userAliasModal__spinnerTitle">
            {formatMessage({
              defaultMessage: 'Please wait, this may take a few seconds...',
              id: 'menu.userAlias.loading'
            })}
          </Loader.Title>
        </Loader>
      ) : (
        <>
          <div
            className={cn('userAliasModal userAliasModal--fadeIn', {
              'userAliasModal--hide': view !== 'list'
            })}
          >
            <main className="userAliasModal__content">
              <dl className="userAliasModal__contentUserInfo">
                <dt className="userAliasModal__contentUserInfoLabel">User:</dt>
                <dd className="userAliasModal__contentUserInfoValue">{getUserName(user, false)}</dd>
                <dt className="userAliasModal__contentUserInfoLabel">Email:</dt>
                <dd className="userAliasModal__contentUserInfoValue">{user.email}</dd>
              </dl>
              <div className="userAliasModal__contentDetails">
                <TaptapList className="userAliasList" data={aliasesCompositionData} id="userAliasList">
                  <TaptapList.ColumnsBar>
                    <TaptapList.ColumnsBar.Column
                      className="userAliasModal__contentDetailsAlias"
                      description={formatMessage({
                        defaultMessage: 'Alias',
                        id: 'menu.userAlias.alias'
                      })}
                      id={'userAliasNameColumn'}
                      key="userAliasName"
                    />
                    <TaptapList.ColumnsBar.Column
                      className="userAliasModal__contentDetailsEmail"
                      description={formatMessage({
                        defaultMessage: 'Email',
                        id: 'menu.userAlias.email'
                      })}
                      id={'userAliasEmailColumn'}
                      key="userAliasEmail"
                    />
                  </TaptapList.ColumnsBar>
                  <TaptapList.RowsListVirtualized<TaptapListItem>
                    emptyStateDescription={formatMessage({
                      defaultMessage: 'You haven’t created any aliases yet. Create a new alias to get started.',
                      id: 'menu.userAlias.emptyState.description'
                    })}
                    emptyStateIcon="fal fa-empty-set"
                    emptyStateTitle={formatMessage({
                      defaultMessage: 'No aliases yet',
                      id: 'menu.userAlias.emptyState.title'
                    })}
                  >
                    {listItems =>
                      listItems.map(item => (
                        <TaptapList.RowsListVirtualized.Row id={item.id} key={item.id} virtualData={item.virtualData}>
                          <div className="userAliasList__contentDetailsAlias" title={item.name}>
                            {item.name}
                          </div>
                          <div className="userAliasList__contentDetailsEmail" title={item.email}>
                            {item.email}
                          </div>
                          <div className="userAliasList__contentDetailsActions">
                            <Checkbox
                              active={item.alias.aliasConfiguration.active}
                              id={`userAliasList_${item.alias._id}_${item.alias.aliasConfiguration.active}`}
                              isSwitch
                              onClickAction={() => handleAliasActiveChange(item.alias)}
                              tiny
                            />
                            <ButtonUI
                              className="userAliasModal__contentDetailsActionsDelete"
                              icon="fal fa-trash"
                              id={`userAliasList-actionsDelete-${item.alias._id}`}
                              onClick={() => handleAliasDelete(item.alias)}
                              shape="link"
                              size="ultraSmall"
                              type="secondary"
                            />
                          </div>
                        </TaptapList.RowsListVirtualized.Row>
                      ))
                    }
                  </TaptapList.RowsListVirtualized>
                </TaptapList>
              </div>
            </main>
            <footer className="userAliasModal__footer">
              <ButtonUI
                onClick={onClose}
                shape="squareRounded"
                size="medium"
                text={formatMessage({
                  defaultMessage: 'Cancel',
                  id: 'common.cancel'
                })}
                type="secondary"
              />
              <ButtonUI
                onClick={() => setView('create')}
                shape="squareRounded"
                size="medium"
                text={formatMessage({
                  defaultMessage: 'Create new alias',
                  id: 'menu.userAlias.createNew.title'
                })}
                type="primary"
              />
            </footer>
          </div>
          <div
            className={cn('userAliasModal__create userAliasModal__create--fadeIn', {
              'userAliasModal__create--hide': view !== 'create'
            })}
          >
            <CreateUserAlias
              aliases={aliases}
              onAliasCreated={handleAliasCreation}
              onBack={() => setView('list')}
              user={user}
            />
          </div>
        </>
      )}
    </ModalUI>
  );
}

export default UserAlias;
